import { useEffect, useMemo, useRef, useState } from 'react';

import CloseIcon from 'assets/icons/CloseIcon';
import { GALLERY_LIST } from 'consts/galleryList';
import { APARTMENT_MAP } from 'consts/building';

import { useGalleryHandle } from 'components/World/hooks/useGalleryHandle';
import { useGallerySource } from 'components/World/hooks/useGallerySource';
import { useHistory } from 'react-router-dom';
const sortByNameAlphabet = (arr) =>
  arr.slice().sort((a, b) => {
    const nameA = a.name?.toLowerCase() || '';
    const nameB = b.name?.toLowerCase() || '';
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });

const imgArr = sortByNameAlphabet(GALLERY_LIST);
const infoIcon = '/assets/images/information-button.png';

const DPGallery = ({ onClose = () => {}, onCloseMenu = () => {} }) => {
  const handleGalleryClick = useGalleryHandle();
  const history = useHistory();
  // Hook để check nguồn mở gallery preview
  // gallerySource.source: 'gallery' | 'hotspot' | null
  // gallerySource.isFromGallery: boolean - true nếu mở từ gallery menu
  // gallerySource.isFromHotspot: boolean - true nếu mở từ click media hotspot
  const gallerySource = useGallerySource();

  // Ví dụ sử dụng: có thể dùng để thay đổi behavior dựa trên nguồn mở
  // if (gallerySource.isFromHotspot) {
  //   // Logic khi mở từ hotspot
  // } else if (gallerySource.isFromGallery) {
  //   // Logic khi mở từ gallery menu
  // }

  const [preview, setPreview] = useState({
    open: false,
    src: null,
    index: null,
  });
  const [galleryFilter, setGalleryFilter] = useState('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);
  const [previewSceneFilter, setPreviewSceneFilter] = useState('all');
  const [isPreviewFilterOpen, setIsPreviewFilterOpen] = useState(false);
  const previewFilterRef = useRef(null);

  // Log để demo (có thể xóa sau)
  useEffect(() => {
    if (preview.open) {
      console.log('Gallery preview source:', gallerySource);
    }
  }, [preview.open, gallerySource]);

  useEffect(() => {
    // Tách slug từ pathname (phần sau /gallery/)
    const pathname = history.location.pathname;
    const slug = pathname.replace(/^\/gallery\/?/, '').replace(/\/$/, '');

    if (!slug) return;

    // Tìm item có label khớp chính xác với slug
    // Ưu tiên items có label dài hơn để tránh match sai (ví dụ: tropical-underwater-2 vs tropical-underwater)
    const sortedItems = [...imgArr].sort(
      (a, b) => b.label.length - a.label.length
    );
    const PreviewImg = sortedItems.find((i) => {
      // So sánh exact match với slug
      return slug === i.label || pathname === `/gallery/${i.label}`;
    });

    if (PreviewImg) {
      const index = imgArr.findIndex((i) => i.label === PreviewImg.label);

      // Chỉ update state nếu item khác với item hiện tại để tránh unnecessary re-render
      setPreview((prev) => {
        // Nếu đã đúng item và đang mở, không cần update
        if (prev.index === index && prev.open) {
          return prev;
        }

        // Nếu index thay đổi, reset filter
        const shouldResetFilter = prev.index !== index && prev.index !== null;
        if (shouldResetFilter) {
          // Reset filter sau khi state update
          setTimeout(() => setPreviewSceneFilter('all'), 0);
        }

        return {
          open: true,
          src: PreviewImg.previewUrl,
          index,
        };
      });
    }
  }, [history.location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
      if (
        previewFilterRef.current &&
        !previewFilterRef.current.contains(event.target)
      ) {
        setIsPreviewFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get unique groups from gallery list
  const filterOptions = [
    { value: 'all', label: 'All Location' },
    { value: 'food-court', label: 'Food Court' },
    { value: 'multifunctional-room', label: 'Multifunctional Room' },
  ];

  // Filter gallery items based on selected filter
  const filteredItems =
    galleryFilter === 'all'
      ? imgArr
      : imgArr.filter((item) => item.group === galleryFilter);

  // Get unique sceneIds for preview filter
  const previewSceneOptions = useMemo(() => {
    const uniqueSceneIds = [
      ...new Set(imgArr.map((item) => item.sceneId).filter(Boolean)),
    ];
    return [
      { value: 'all', label: 'All Location' },
      ...uniqueSceneIds.map((sceneId) => ({
        value: sceneId,
        label: APARTMENT_MAP[sceneId]?.label || `Scene ${sceneId.slice(-6)}`,
      })),
    ];
  }, []);

  // Filter preview thumbnails based on sceneId
  const filteredPreviewItems =
    previewSceneFilter === 'all'
      ? imgArr
      : imgArr.filter((item) => item.sceneId === previewSceneFilter);

  // Update preview index when filter changes
  useEffect(() => {
    if (preview.open && preview.index !== null) {
      const currentItem = imgArr[preview.index];
      const isCurrentItemInFilter = filteredPreviewItems.some(
        (item) => item.label === currentItem?.label
      );

      if (!isCurrentItemInFilter && filteredPreviewItems.length > 0) {
        // Nếu item hiện tại không còn trong filtered list, chuyển sang item đầu tiên
        const firstItem = filteredPreviewItems[0];
        const firstIndex = imgArr.findIndex(
          (item) => item.label === firstItem.label
        );
        setPreview({
          open: true,
          src: firstItem.previewUrl,
          index: firstIndex,
        });
        // Preserve state khi navigate giữa các gallery items để giữ nguyên previousUrl ban đầu
        handleGalleryClick(firstItem.label, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSceneFilter]);

  const handleSelect = (img, index) => {
    setPreview({
      open: true,
      src: img,
      index,
    });
  };

  const handleNext = () => {
    setPreview((prev) => {
      if (prev.index == null) return prev;
      const currentItems =
        previewSceneFilter === 'all'
          ? imgArr
          : imgArr.filter((item) => item.sceneId === previewSceneFilter);
      const currentItemIndex = currentItems.findIndex(
        (item) => item.label === imgArr[prev.index].label
      );
      if (currentItemIndex === -1) return prev;
      const nextIndexInFiltered = (currentItemIndex + 1) % currentItems.length;
      const nextItem = currentItems[nextIndexInFiltered];
      const nextIndex = imgArr.findIndex(
        (item) => item.label === nextItem.label
      );
      // Preserve state khi navigate giữa các gallery items để giữ nguyên previousUrl ban đầu
      handleGalleryClick(nextItem.label, true);
      return {
        open: true,
        src: nextItem,
        index: nextIndex,
      };
    });
  };

  const handlePrev = () => {
    setPreview((prev) => {
      if (prev.index == null) return prev;
      // Filter items theo sceneId filter hiện tại
      const currentItems =
        previewSceneFilter === 'all'
          ? imgArr
          : imgArr.filter((item) => item.sceneId === previewSceneFilter);
      const currentItemIndex = currentItems.findIndex(
        (item) => item.label === imgArr[prev.index].label
      );
      if (currentItemIndex === -1) return prev;
      const nextIndexInFiltered =
        (currentItemIndex - 1 + currentItems.length) % currentItems.length;
      const nextItem = currentItems[nextIndexInFiltered];
      const nextIndex = imgArr.findIndex(
        (item) => item.label === nextItem.label
      );
      // Preserve state khi navigate giữa các gallery items để giữ nguyên previousUrl ban đầu
      handleGalleryClick(nextItem.label, true);
      return {
        open: true,
        src: nextItem,
        index: nextIndex,
      };
    });
  };

  return (
    <div className="dp-gallery-container-wrapper">
      {preview.open && preview.src && (
        <div className="dp-gallery-preview">
          <div className="dpdoc-logo">
            <img src="/assets/images/logo/etown_logo.png" alt="logo" />
          </div>
          <button
            className="dp-gallery-preview__close"
            onClick={() => {
              // Nếu có previousUrl (mở từ hotspot/scene), quay về URL trước đó
              // Ưu tiên previousUrl từ gallerySource, nếu không có thì thử lấy từ state
              const previousUrl =
                gallerySource.previousUrl ||
                history.location.state?.previousUrl ||
                new URLSearchParams(history.location.search).get('previousUrl');

              const isFromHotspot =
                gallerySource.isFromHotspot ||
                history.location.state?.fromHotspot === true ||
                new URLSearchParams(history.location.search).get(
                  'fromHotspot'
                ) === 'true';

              if (isFromHotspot && previousUrl) {
                // Decode previousUrl nếu là từ query params
                const decodedPreviousUrl = previousUrl.startsWith('/')
                  ? previousUrl
                  : decodeURIComponent(previousUrl);
                history.push(decodedPreviousUrl);
                onClose();
                onCloseMenu();
              } else {
                // Logic hiện tại: chỉ tắt preview và về /gallery
                history.push('/gallery');
              }
              setPreview((prev) => ({
                ...prev,
                open: false,
              }));
            }}
          >
            ×
          </button>

          <div className="dp-gallery-preview__image-container">
            <div className="dp-gallery-preview__filter" ref={previewFilterRef}>
              <div
                className="gallery-filter-dropdown"
                onClick={() => setIsPreviewFilterOpen(!isPreviewFilterOpen)}
              >
                <span className="gallery-filter-dropdown__text">
                  {previewSceneFilter === 'all'
                    ? 'All Location'
                    : previewSceneOptions.find(
                        (opt) => opt.value === previewSceneFilter
                      )?.label || 'All Location'}
                </span>
                <span
                  className={`gallery-filter-dropdown__chevron ${
                    isPreviewFilterOpen ? 'open' : ''
                  }`}
                >
                  ▼
                </span>
              </div>
              {isPreviewFilterOpen && (
                <div className="gallery-filter-dropdown__options">
                  {previewSceneOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`gallery-filter-dropdown__option ${
                        previewSceneFilter === option.value ? 'active' : ''
                      }`}
                      onClick={() => {
                        setPreviewSceneFilter(option.value);
                        setIsPreviewFilterOpen(false);
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              className="dp-gallery-preview__nav dp-gallery-preview__nav--prev"
              onClick={handlePrev}
            >
              ‹
            </button>

            <div className="dp-gallery-preview__image-wrapper">
              <div className="relative">
                <div className="dp-gallery-preview__image-box">
                  <div className="img-title">
                    <span>{imgArr[preview.index].name}</span>
                  </div>
                  <img src={imgArr[preview.index].previewUrl} alt="preview" />
                  <a
                    className="info_button"
                    href={imgArr[preview.index].imgUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={infoIcon} alt="info" />
                  </a>
                </div>
              </div>
            </div>
            <button
              className="dp-gallery-preview__nav dp-gallery-preview__nav--next"
              onClick={handleNext}
            >
              ›
            </button>
          </div>

          <div className="dp-gallery-preview__thumbs-container">
            <div className="dp-gallery-preview__thumbs">
              {filteredPreviewItems.map((img, index) => {
                const originalIndex = imgArr.findIndex(
                  (item) => item.label === img.label
                );
                return (
                  <button
                    key={img.label}
                    className={`dp-gallery-preview__thumb ${
                      preview.index === originalIndex ? 'active' : ''
                    }`}
                    onClick={() => {
                      handleSelect(img.previewUrl, originalIndex);
                      // Preserve state khi navigate giữa các gallery items để giữ nguyên previousUrl ban đầu
                      handleGalleryClick(img.label, true);
                    }}
                  >
                    <img src={img.resizeUrl} alt={`thumb-${index}`} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="dpdoc-grid etown-gallery-container">
        <button
          className="dp-gallery-back-button"
          onClick={() => {
            onClose();
          }}
        >
          <CloseIcon color="white" />
        </button>
        <div className="dpdoc-logo">
          <img src="/assets/images/logo/etown_logo.png" alt="logo" />
        </div>
        <div className="dpdoc-header">
          <div className="dpdoc-header-title">
            <span>Gallery</span>
          </div>
          <div className="gallery-filter" ref={filterRef}>
            <div
              className="gallery-filter-dropdown"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <span className="gallery-filter-dropdown__text">
                {galleryFilter === 'all'
                  ? 'All Location'
                  : filterOptions.find((opt) => opt.value === galleryFilter)
                      ?.label || 'All Location'}
              </span>
              <span
                className={`gallery-filter-dropdown__chevron ${
                  isFilterOpen ? 'open' : ''
                }`}
              >
                ▼
              </span>
            </div>
            {isFilterOpen && (
              <div className="gallery-filter-dropdown__options">
                {filterOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`gallery-filter-dropdown__option ${
                      galleryFilter === option.value ? 'active' : ''
                    }`}
                    onClick={() => {
                      setGalleryFilter(option.value);
                      setIsFilterOpen(false);
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {filteredItems.map((img, index) => {
          const originalIndex = imgArr.findIndex(
            (item) => item.label === img.label
          );
          return (
            <div
              key={img.label}
              className={`dpdoc-card ${
                preview.open && preview.index === originalIndex ? 'active' : ''
              }`}
              onClick={() => {
                handleSelect(img.previewUrl, originalIndex);
                handleGalleryClick(img.label);
              }}
            >
              <img src={img.resizeUrl} alt="gallery" />
              <div className="dpdoc-card-title">
                <span>{img.name}</span>
              </div>
              <div className="dpdoc-card-group">
                <span>{img.groupLabel}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DPGallery;
